'use client'

import React, { useState, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import Image from 'next/image'
import RegistrationNumber from '@components/GICar/RegistrationNumber'
import { useGICar } from '@context/GICarContext/GICarContext'
import { useUserContext } from '@context/UserContext/UserContenxt'
import HeaderDesktopAndMweb from '@components/Product/ProductNav/headerDesktopAndMweb'
import IllogicalFlowModal from '@components/common/IllogicalFlowModal/illogicalFlowModal'
import { AutoProduct } from 'src/enums/AutoProduct'
import { trackEvent, getCommonEventProperties } from '@acko-tech/building-blocks.ui.acko-analytics'
import { getUrlWithQueryParams } from '@utils/queryParamUtils'
import Cookies from 'js-cookie'
import { callWebviewHandler, WebViewActions } from '@utils/webview'
import { formatPremium, getContinueInsuringCardsByProduct, getUrgentRenewalCardsByProduct } from '@utils/giCar/utils'
import { cleanRegNo } from '@utils/giCar/registrationValidation'
import { GICarLandingPageProps, RecentSearchData, RenewalPlanData } from '@interfaces/giCar'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import Award from '@components/Home/Hero/Award'
import TrustHomeVideoPlayers from '@components/Home/VideoPlayer'
import { parseBoldText } from '@utils/parseBoldText'
import { isGiCarPageV1 } from '@utils/giCarPageVersion'
import logger from '@utils/logger'
import ContinueInsureCard from './giCarWidgets/ContinueInsureCard'
import UrgentRenewalCard from './giCarWidgets/UrgentRenewalCard'
import {
  CONSTANTS,
  IMAGES,
  TEXT,
  URLS,
  ANALYTICS,
  QUERY_PARAMS,
  ERROR_MESSAGES,
  DEFAULTS,
  ADDITIONAL_IMAGES,
  VIDEO_DATA,
  FORM,
} from './config'
import styles from './GICarLanding.module.scss'

const GICarLandingPage: React.FC<GICarLandingPageProps> = props => {
  const isMobile = props.isMobile ?? false
  const renewalPlans = props.renewalPlans ?? []
  const regexPatternsData = props.regexPatternsData ?? []
  const searchParams = useSearchParams()
  const giCarContext = useGICar()
  const { userData } = useUserContext()
  const mobileNumber = userData?.phone || props.mobileNumber || ''
  const [showIllogicalModal, setShowIllogicalModal] = useState(false)
  const formContainerRef = useRef<HTMLDivElement>(null)
  const urlCarNumber = searchParams.get(QUERY_PARAMS.CAR_NUMBER) || ''
  const animatePlaceholder = props.animatePlaceholder ?? false
  const isV1 = isGiCarPageV1(props.version)

  const urgentCards = getUrgentRenewalCardsByProduct(renewalPlans, 'car')
  const continueCards = getContinueInsuringCardsByProduct(props.recentSearches || [], 'car')

  const totalMobileCards = urgentCards.length + continueCards.length
  const isSingleMobileCard = totalMobileCards === 1

  const radius = CONSTANTS.CIRCLE_RADIUS
  const circumference = 2 * Math.PI * radius
  const topOffset = -(circumference / 4)

  const handleSuccessfulSubmission = (proposalData: any) => {
    giCarContext.setProposalData(proposalData)
    if (proposalData?.redirect_url) {
      window.location.href = getUrlWithQueryParams(proposalData?.redirect_url)
      return
    }
    if (proposalData?.ekey) {
      const registrationNumber = cleanRegNo(
        proposalData?.registration_number || giCarContext.registrationNumber || urlCarNumber || ''
      )
      const redirectUrl = URLS.FRESH_CAR_TEMPLATE(proposalData.ekey, registrationNumber)
      window.location.href = getUrlWithQueryParams(redirectUrl)
    }
  }

  const handleSubmissionError = (error: any) => {
    logger.error('Submission error:', error)
    if (error.type === 'illogical_flow' || giCarContext.illogicalFlowData) {
      setShowIllogicalModal(true)
    }
  }

  const handleRetrySubmission = () => {
    giCarContext.resetErrors()
    giCarContext.resetProposalData()
    setShowIllogicalModal(false)
  }
  const handleRenewalClick = (planOrProposalId?: RenewalPlanData | string) => {
    const proposalId = typeof planOrProposalId === 'string' ? planOrProposalId : planOrProposalId?.proposal_id
    if (!proposalId) {
      logger.error(ERROR_MESSAGES.NO_PROPOSAL_ID)
      return
    }

    if (typeof window !== 'undefined') {
      try {
        void trackEvent(ANALYTICS.EVENTS.TAP_RENEWAL_CARD, {
          ...getCommonEventProperties('seo'),
          header_name: ANALYTICS.HEADER_NAMES[props.version || 'V1'],
          product: ANALYTICS.PRODUCT.AUTO_CAR,
          funnel: ANALYTICS.FUNNEL.RENEWAL_ENTRY,
          action_type: 'Renewal',
          title: ANALYTICS.PAGE_TITLE,
          proposal_id: proposalId,
        })
      } catch (error) {
        logger.error('Failed to track tap_renewal_card event:', error)
      }
      const renewalUrl = URLS.RENEWAL_TEMPLATE(proposalId)
      window.location.href = getUrlWithQueryParams(renewalUrl)
    }
  }

  const handleContinueInsureClick = (item?: RecentSearchData) => {
    let continueUrl: string | null = null
    if (item?.redirect_link) {
      continueUrl = item?.redirect_link
    } else if (item?.proposal_id) {
      continueUrl = URLS.CONTINUE_INSURE_TEMPLATE(item.proposal_id)
    } else {
      logger.error(ERROR_MESSAGES.NO_REDIRECT_LINK)
      return
    }

    if (typeof window !== 'undefined') {
      try {
        void trackEvent(ANALYTICS.EVENTS.TAP_CONTINUE_INSURE_CARD, {
          ...getCommonEventProperties('seo'),
          header_name: ANALYTICS.HEADER_NAMES[props.version || 'V1'],
          product: ANALYTICS.PRODUCT.AUTO_CAR,
          funnel: ANALYTICS.FUNNEL.CONTINUE_ENTRY,
          action_type: 'Continue',
          title: ANALYTICS.PAGE_TITLE,
          proposal_id: item.proposal_id,
          registration_number: item?.registration_number || '',
        })
      } catch (error) {
        logger.error('Failed to track tap_continue_insure_card event:', error)
      }
      window.location.href = getUrlWithQueryParams(continueUrl)
    }
  }

  const handleBackClick = () => {
    if (typeof window === 'undefined') return
    if (Cookies.get('webview')) {
      callWebviewHandler({ action: WebViewActions.CLOSE_WEBVIEW })
    } else {
      window.location.href = getUrlWithQueryParams(URLS.HOME)
    }
  }

  const handleBookedNewCarClick = () => {
    if (typeof window !== 'undefined') {
      try {
        void trackEvent(ANALYTICS.EVENTS.TAP_NEW_VEHICLE_CTA, {
          ...getCommonEventProperties('seo'),
          header_name: ANALYTICS.HEADER_NAMES[props.version || 'V1'],
          product: ANALYTICS.PRODUCT.AUTO_CAR,
          funnel: ANALYTICS.FUNNEL.ENTRY,
        })
      } catch (error) {
        logger.error('Failed to track tap_new_vehicle_cta event:', error)
      }
      window.location.href = getUrlWithQueryParams(URLS.NEW_CAR)
    }
  }

  const handleMainScrollWheel = (event: React.WheelEvent<HTMLElement>) => {
    if (typeof window === 'undefined' || window.innerWidth <= CONSTANTS.DESKTOP_BREAKPOINT) return
    const container = formContainerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const canScrollDown = scrollTop + clientHeight < scrollHeight - CONSTANTS.SCROLL_THRESHOLD
    const canScrollUp = scrollTop > 0

    if ((event.deltaY > 0 && canScrollDown) || (event.deltaY < 0 && canScrollUp)) {
      event.preventDefault()
      container.scrollTop += event.deltaY
    }
  }

  const handleBuyingNewCarClick = () => {
    if (typeof window !== 'undefined') {
      try {
        void trackEvent(ANALYTICS.EVENTS.TRACK_EVENT_COMPLETE, {
          ...getCommonEventProperties('seo'),
          header_name: ANALYTICS.HEADER_NAMES[props.version || 'V1'],
          product: ANALYTICS.PRODUCT.AUTO_CAR,
          funnel: ANALYTICS.FUNNEL.ENTRY,
          title: ANALYTICS.PAGE_TITLE,
          journey: ANALYTICS.JOURNEY.VIEW_PAGE_BUY_NEW_CAR_ICON,
        })
      } catch (error) {
        logger.error('Failed to track tap_new_vehicle_cta event:', error)
      }
      window.location.href = getUrlWithQueryParams(URLS.ACKODRIVE)
    }
  }

  return (
    <>
      <div className={styles.hideOnMobile}>
        <HeaderDesktopAndMweb isMobile={isMobile} />
      </div>
      <div data-version={!isV1 ? 'v2' : undefined} className={styles.pageContainer}>
        <main className={styles.mainContent} onWheel={handleMainScrollWheel}>
          <div className={styles.mobileHeroSection}>
            <button type="button" className={styles.mobileBackButton} onClick={handleBackClick} aria-label="Go back">
              <PropertyControlledComponent
                controllerProperty={isV1}
                fallback={
                  <svg viewBox="0 0 11 24" fill="none">
                    <path
                      d="M9.82623 18.8858C9.4357 19.2763 8.78051 19.3 8.36281 18.9387L1.32827 12.8538C0.910575 12.4924 0.888546 11.883 1.27907 11.4924L7.85595 4.91556C8.24647 4.52504 8.90167 4.50136 9.31937 4.86267C9.73707 5.22398 9.7591 5.83346 9.36857 6.22399L3.4988 12.0938L9.77702 17.5244C10.1947 17.8858 10.2168 18.4952 9.82623 18.8858Z"
                      fill="#141414"
                    />
                  </svg>
                }>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="#1a1a1a"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </PropertyControlledComponent>
            </button>
            <PropertyControlledComponent
              controllerProperty={isV1}
              fallback={
                <div className={styles.mobileHero}>
                  <h1 className={styles.headingLine}>{parseBoldText(TEXT.V2_HEADING, styles.highlight)}</h1>
                  <Image {...IMAGES.V2_CAR_MOBILE} />
                </div>
              }>
              <h1 className={styles.mobileMainHeading}>{TEXT.HEADINGS.MOBILE_MAIN}</h1>
            </PropertyControlledComponent>

            <PropertyControlledComponent controllerProperty={totalMobileCards > 0} fallback={null}>
              <div
                className={styles.mobileCardsCarousel}
                style={isSingleMobileCard ? { justifyContent: 'flex-start', overflowX: 'hidden' } : undefined}>
                {urgentCards.map(plan => (
                  <UrgentRenewalCard
                    key={
                      plan?.proposal_id ||
                      `${plan?.mmv?.make || plan?.vehicle_details?.make || ''} ${
                        plan?.mmv?.model || plan?.vehicle_details?.model || ''
                      }`
                    }
                    plan={plan}
                    onClick={handleRenewalClick}
                    style={isSingleMobileCard ? { width: '100%', maxWidth: '100%' } : undefined}
                    circumference={circumference}
                    topOffset={topOffset}
                    RENEWAL_THRESHOLD_DAYS={CONSTANTS.RENEWAL_THRESHOLD_DAYS}
                  />
                ))}
                {continueCards.map((item, index) => (
                  <ContinueInsureCard
                    key={item?.redirect_link || item?.proposal_id || index}
                    item={item}
                    onClick={handleContinueInsureClick}
                    style={isSingleMobileCard ? { width: '100%', maxWidth: '100%' } : undefined}
                    formatPremium={formatPremium}
                    index={index}
                  />
                ))}
              </div>
            </PropertyControlledComponent>
            <div className={styles.mobileRegistrationCard}>
              <h2 className={styles.formLabel}>{TEXT.FORM_LABEL[props.version || 'V1']}</h2>
              <RegistrationNumber
                onSuccess={handleSuccessfulSubmission}
                onError={handleSubmissionError}
                mobileNumber={mobileNumber}
                autoFillNumber={urlCarNumber}
                autoSubmit={DEFAULTS.AUTO_SUBMIT}
                regexPatternsData={regexPatternsData}
                formData={FORM[props.version || 'V1']}
                headerName={ANALYTICS.HEADER_NAMES[props.version || 'V1']}
                animatePlaceholder={animatePlaceholder}
              />
            </div>
            <div className={styles.uidContainer}>{TEXT.UID}</div>
          </div>
          <div className={`${styles.heroContainer} ${styles.hideOnMobile}`}>
            <div className={styles.trustBadges}>
              {TEXT.AWARDS.map((award, index) => (
                <Award key={index}>{award}</Award>
              ))}
            </div>

            <PropertyControlledComponent
              controllerProperty={isV1}
              fallback={<h1 className={styles.headingLine}>{parseBoldText(TEXT.V2_HEADING, styles.highlight)}</h1>}>
              <h1 className={styles.mainHeading}>
                <div className={styles.headingLine}>
                  <span className={styles.headsem}>{TEXT.HEADINGS.DESKTOP.LETS}</span>
                  <span className={styles.headstrength}>{TEXT.HEADINGS.DESKTOP.INSURE}</span>
                </div>
                <div className={styles.headingLine}>
                  <span className={styles.headyour}>{TEXT.HEADINGS.DESKTOP.YOUR}</span>
                  <span className={styles.headcapital}>{TEXT.HEADINGS.DESKTOP.CAR}</span>
                </div>
              </h1>
            </PropertyControlledComponent>

            <div className={styles.carImageContainer}>
              <Image {...(isV1 ? IMAGES.CAR_WITH_BOW : IMAGES.V2_CAR_IMAGE)} priority className={styles.carImage} />
            </div>
          </div>

          <div className={styles.formContainer} ref={formContainerRef}>
            <PropertyControlledComponent controllerProperty={urgentCards.length > 0} fallback={null}>
              {urgentCards.map(plan => (
                <UrgentRenewalCard
                  key={
                    plan?.proposal_id ||
                    `${plan?.mmv?.make || plan?.vehicle_details?.make || ''} ${
                      plan?.mmv?.model || plan?.vehicle_details?.model || ''
                    }`
                  }
                  plan={plan}
                  onClick={handleRenewalClick}
                  className={styles.hideOnMobile}
                  circumference={circumference}
                  topOffset={topOffset}
                  RENEWAL_THRESHOLD_DAYS={CONSTANTS.RENEWAL_THRESHOLD_DAYS}
                />
              ))}
            </PropertyControlledComponent>
            <PropertyControlledComponent controllerProperty={continueCards.length > 0} fallback={null}>
              {continueCards.map((item, index) => (
                <ContinueInsureCard
                  key={item?.redirect_link || item?.proposal_id || index}
                  item={item}
                  onClick={handleContinueInsureClick}
                  className={styles.hideOnMobile}
                  formatPremium={formatPremium}
                  index={index}
                />
              ))}
            </PropertyControlledComponent>

            <div className={`${styles.registrationCard} ${styles.hideOnMobile}`}>
              <div className={styles.registrationCardContent}>
                <h2 className={styles.formLabel}>{TEXT.FORM_LABEL[props.version || 'V1']}</h2>
                <RegistrationNumber
                  onSuccess={handleSuccessfulSubmission}
                  onError={handleSubmissionError}
                  mobileNumber={mobileNumber}
                  autoFillNumber={urlCarNumber}
                  autoSubmit={DEFAULTS.AUTO_SUBMIT}
                  regexPatternsData={regexPatternsData}
                  formData={FORM[props.version || 'V1']}
                  headerName={ANALYTICS.HEADER_NAMES[props.version || 'V1']}
                  animatePlaceholder={animatePlaceholder}
                />
              </div>
              <div className={styles.uidContainer}>{TEXT.UID}</div>
            </div>
            <div className={styles.didYouKnowCard}>
              <div className={styles.didYouKnowHeader}>
                <p className={styles.didYouKnowTitle}>
                  {parseBoldText(TEXT.CAR_CARDS_SECTION[props.version || 'V1'].SECTION_HEADING, styles.highlight)}
                </p>

                <div className={styles.mobileDidYouKnowImage}>
                  <Image
                    src={IMAGES.CAR_MOBILE.url}
                    alt={IMAGES.CAR_MOBILE.alt}
                    width={IMAGES.CAR_MOBILE.width}
                    height={IMAGES.CAR_MOBILE.height}
                    className={styles.smallCarImage}
                  />
                </div>
              </div>

              <div className={styles.carCardsGrid}>
                {TEXT.CAR_CARDS_SECTION[props.version || 'V1'].CARDS.map(card => (
                  <div
                    className={`${styles.carCard} ${card.FEATURED ? styles.featuredCard : ''}`}
                    onClick={card.FEATURED ? handleBookedNewCarClick : handleBuyingNewCarClick}>
                    <h3>{card.TITLE}</h3>
                    <div className={styles.carCardContent}>
                      {card.SUBTITLE && <h4>{card.SUBTITLE}</h4>}
                      <p>{card.DESCRIPTION}</p>
                    </div>
                    {card.BADGE_TEXT && (
                      <div className={styles.saveBadge}>
                        <div className={`${styles.checkIcon} ${styles.hideOnMobile}`}>
                          <Image
                            alt={ADDITIONAL_IMAGES.TICK_ICON.alt}
                            loading="lazy"
                            width={ADDITIONAL_IMAGES.TICK_ICON.width}
                            height={ADDITIONAL_IMAGES.TICK_ICON.height}
                            decoding="async"
                            data-nimg="1"
                            src={ADDITIONAL_IMAGES.TICK_ICON.url}
                          />
                        </div>
                        <span>{card.BADGE_TEXT}</span>
                      </div>
                    )}
                    <button type="button" className={styles.cardLink}>
                      {card.IMG_SRC && (
                        <Image
                          className={styles.ackoDriveLogo}
                          src={card.IMG_SRC}
                          alt={card.TITLE}
                          width={108}
                          height={18}
                        />
                      )}
                      {card.TEXT && <span>{card.TEXT}</span>}
                      <svg
                        className={styles.arrowIcon}
                        width="1em"
                        height="1em"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M15.0001 17L20 12L15 7"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"></path>
                        <path
                          d="M4 12H20"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"></path>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>

        <PropertyControlledComponent
          controllerProperty={showIllogicalModal && giCarContext.illogicalFlowData}
          fallback={null}>
          {giCarContext.illogicalFlowData && (
            <IllogicalFlowModal
              errorCode={giCarContext.illogicalFlowData.error_code}
              illogicalFlowData={giCarContext.illogicalFlowData}
              product={AutoProduct.Car as any}
              regNo={giCarContext.illogicalFlowData.registration_number || urlCarNumber || ''}
              identifiedVehicleType={giCarContext.illogicalFlowData.vehicle_type || CONSTANTS.DEFAULT_VEHICLE_TYPE}
              showIllogicalFlowModal={showIllogicalModal}
              setShowIllogicalFlowModal={setShowIllogicalModal}
              onClose={handleRetrySubmission}
              secondaryCta={handleRetrySubmission}
              setProposalAPIResponse={giCarContext.setProposalData}
            />
          )}
        </PropertyControlledComponent>

        <PropertyControlledComponent controllerProperty={!isV1} fallback={null}>
          <div className={styles.videoContainer}>
            <TrustHomeVideoPlayers videoPlayerData={[VIDEO_DATA]} />
          </div>
        </PropertyControlledComponent>
      </div>
    </>
  )
}

export default GICarLandingPage
